import React from 'react';
import {
  fetchSlideImageStorageUsage,
  formatSlideStorageUsageLabel,
  SLIDE_IMAGE_STORAGE_CHANGED_EVENT,
  SLIDE_IMAGE_STORAGE_LIMIT_BYTES,
  type SlideImageStorageUsage,
} from '../services/slideImageStorageService';
import { SUBSCRIPTION_PASS_CHANGED_EVENT } from '../utils/subscriptionPassStorage';
import { PLAN_PERMISSIONS_CHANGED_EVENT } from './usePlanGateRows';

export function useSlideImageStorageUsage(enabled: boolean): {
  usage: SlideImageStorageUsage | null;
  label: string | null;
  refresh: () => void;
} {
  const [usage, setUsage] = React.useState<SlideImageStorageUsage | null>(null);

  const refresh = React.useCallback(() => {
    if (!enabled) {
      setUsage(null);
      return;
    }
    void fetchSlideImageStorageUsage()
      .then((u) => setUsage(u))
      .catch(() => setUsage({ bytesUsed: 0, bytesLimit: SLIDE_IMAGE_STORAGE_LIMIT_BYTES }));
  }, [enabled]);

  /** MainLayout 마운트·로그인 시 1회 (슬라이드 탭 복귀 시 레이아웃이 다시 마운트됨) */
  React.useEffect(() => {
    if (!enabled) return;
    refresh();
  }, [enabled, refresh]);

  /** 메인 화면에 있는 동안 Storage가 바뀐 경우만 (슬라이드 저장은 보통 탭 전환 후 마운트로 반영) */
  React.useEffect(() => {
    if (!enabled) return;
    const onChanged = () => refresh();
    window.addEventListener(SLIDE_IMAGE_STORAGE_CHANGED_EVENT, onChanged);
    // 이용권이 바뀌면 저장 용량 한도(프리 10MB / 이용권 100MB)도 바뀐다
    window.addEventListener(SUBSCRIPTION_PASS_CHANGED_EVENT, onChanged);
    window.addEventListener(PLAN_PERMISSIONS_CHANGED_EVENT, onChanged);
    return () => {
      window.removeEventListener(SLIDE_IMAGE_STORAGE_CHANGED_EVENT, onChanged);
      window.removeEventListener(SUBSCRIPTION_PASS_CHANGED_EVENT, onChanged);
      window.removeEventListener(PLAN_PERMISSIONS_CHANGED_EVENT, onChanged);
    };
  }, [enabled, refresh]);

  const label = React.useMemo(() => {
    if (!enabled || !usage) return null;
    return formatSlideStorageUsageLabel(usage);
  }, [enabled, usage]);

  return { usage, label, refresh };
}
